import type { AdminUser, RoomRole, Romvalg } from '../../api/useAdminUsers.ts';
import { romNavn, type Rom } from '../../skall/rom.ts';

/**
 * Panelbrukere som ren modell (M4.8 steg 3, `admin-panelbrukere.jsx`): ÉN kilde for lista,
 * filterets tall, tilgangslinjen, personsidens valg og konsekvenssetningen, så de kan ikke gå
 * fra hverandre (wireframens «essensen er raden»). Ingen React her — det er ordene og reglene.
 */

export type Status = 'aktiv' | 'venter' | 'ute';

/** Rollen i ord. «Rolle» brukes bare om det globale laget (DD-60 presisert 2026-09-25). */
export const ROLLE = {
  eier: { ord: 'Eier', ikon: 'key-outline' },
  medlem: { ord: 'Medlem', ikon: 'person-outline' },
} as const;

/**
 * Tilgangen i ett rom i ord. Boblen med linjer er behandlerens; rommet Innspill har den tomme.
 * `bidragsyter` finnes bare i Hypoteser (H1.3) og er et valg bare der (H2.3). Pennen er
 * «skriver sine egne», som i rommets innsending.
 */
export const ROMROLLE: Record<RoomRole, { ord: string; ikon: string }> = {
  behandler: { ord: 'Behandler', ikon: 'chatbubble-ellipses-outline' },
  leser: { ord: 'Leser', ikon: 'eye-outline' },
  bidragsyter: { ord: 'Bidragsyter', ikon: 'create-outline' },
};
export const INGEN_IKON = 'remove-circle-outline';

/** Rommet der `bidragsyter` er et valg (H1.3). */
export const HYPOTESER_ROM: Rom = 'hypoteser';
export const ALLE_IKON = 'grid-outline';

export const erEier = (b: AdminUser) => b.role === 'eier';

export const statusFor = (b: AdminUser): Status => (b.disabledAt ? 'ute' : b.activatedAt ? 'aktiv' : 'venter');

/** Rom-rollene som kart — eier har ingen (409 `owner_has_all_rooms`), hun har alt. */
export const romvalgFor = (b: AdminUser): Romvalg => Object.fromEntries(b.roomAccess.map((r) => [r.room, r.role])) as Romvalg;

/**
 * «12. jan», «3. mars», «18. sep» — uten punktum etter måneden, og med år bare når det ikke er
 * i år. Konseptets ordlyd; `nb-NO` gir «jan.» og «sep.».
 */
export function dato(iso: string, naa = new Date()): string {
  const d = new Date(iso);
  const iAar = d.getFullYear() === naa.getFullYear();
  const tekst = d.toLocaleDateString('nb-NO', iAar ? { day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short', year: 'numeric' });
  return tekst.replace(/(\p{L})\.(?=\s|$)/u, '$1');
}

/** Statuslinjen: ikon og ord (DD-08), aldri farge alene. */
export function statuslinje(b: AdminUser): { ikon: string; tekst: string } {
  const s = statusFor(b);
  if (s === 'ute') return { ikon: 'close-circle-outline', tekst: `Deaktivert ${dato(b.disabledAt!)}` };
  if (s === 'venter') return { ikon: 'time-outline', tekst: `Venter på første innlogging · lagt til ${dato(b.createdAt)}` };
  return { ikon: 'checkmark-circle-outline', tekst: `Aktiv siden ${dato(b.activatedAt!)}` };
}

/**
 * Har personen tilgang til rommet — filterets og tellingens ene definisjon: eier har alt,
 * deaktiverte teller ikke. «Alle» tar de deaktiverte med igjen.
 */
export const harTilgang = (b: AdminUser, rom: Rom) => statusFor(b) !== 'ute' && (erEier(b) || b.roomAccess.some((r) => r.room === rom));

/**
 * Tilgangslinjens grupper: etter rolle, Behandler før Leser før Bidragsyter (bare Hypoteser,
 * H1.3), og gruppen med det valgte rommet
 * først når filteret er satt (godkjent 2026-09-25). Rommene i leietakerens rekkefølge.
 */
export function grupper(valg: Romvalg, rommene: readonly Rom[], forst: Rom | null = null): Array<{ rolle: RoomRole; rom: Rom[] }> {
  const g = (['behandler', 'leser', 'bidragsyter'] as const).map((rolle) => ({ rolle, rom: rommene.filter((r) => valg[r] === rolle) })).filter((x) => x.rom.length > 0);
  if (forst) g.sort((a, b) => Number(b.rom.includes(forst)) - Number(a.rom.includes(forst)));
  return g;
}

/** «A, B og C» — setningen skrives av valgene, aldri av en egen streng. */
export const liste = (ord: readonly string[]) => (ord.length <= 1 ? ord.join('') : `${ord.slice(0, -1).join(', ')} og ${ord[ord.length - 1]}`);

/** Hva et rom har, i ubestemt og bestemt flertall: saker overalt, hypoteser i Hypoteser. */
const TINGENE = (rom: Rom): readonly [string, string] => (rom === 'hypoteser' ? ['hypoteser', 'hypotesene'] : ['saker', 'sakene']);

/**
 * Konsekvenssetningen (P2, DD-72 presisert): skrives om fra valgene, rom for rom. Én setning
 * per person, aldri en dialog. Medlem uten rom er et VARSEL (ikon og tekst, DD-08), og eier får
 * den fulle lista over det tyngste hun får.
 */
export function setningFor(epost: string, eier: boolean, valg: Romvalg, rommene: readonly Rom[], ny: boolean): { ikon: string; tekst: string; varsel?: true } {
  const start = ny ? 'Fra første innlogging ' : '';
  const slutt = ny ? '' : ' Gjelder fra neste gang panelet hentes.';
  if (eier) return { ikon: ROLLE.eier.ikon, tekst: `${start}${epost} får alt i alle rom og hele Organisasjon — også denne lista, tofaktor-bryteren og API-nøklene.${slutt}` };
  const behRom = rommene.filter((r) => valg[r] === 'behandler');
  const beh = behRom.map(romNavn);
  const les = rommene.filter((r) => valg[r] === 'leser').map(romNavn);
  const bidr = rommene.filter((r) => valg[r] === 'bidragsyter').map(romNavn);
  const ingen = rommene.filter((r) => !valg[r]).map(romNavn);
  if (!beh.length && !les.length && !bidr.length) return { varsel: true, ikon: 'warning-outline', tekst: `${epost} kan logge inn, men ser ingenting i panelet før et rom er valgt.` };
  const deler: string[] = [];
  if (rommene.length === 1 && !bidr.length) {
    // Ett rom: ordet «Innspill» finnes ikke i et skall med ett rom (konseptets skjerm 2).
    const [ent, best] = TINGENE(rommene[0]!);
    deler.push(beh.length ? `svarer på ${ent} og setter status` : `ser ${best} uten å endre noe`);
  } else {
    // Ett ledd per slags ting: saker i Innspill, hypoteser i Hypoteser.
    for (const ent of new Set(behRom.map((r) => TINGENE(r)[0]))) deler.push(`svarer på ${ent} og setter status i ${liste(behRom.filter((r) => TINGENE(r)[0] === ent).map(romNavn))}`);
    if (les.length) deler.push(`ser ${liste(les)} uten å endre noe`);
    // H1.3: bidragsyter sender inn, ser egne og leser registeret — aldri notater, aldri en sak.
    if (bidr.length) deler.push(`sender inn hypoteser og leser registeret i ${liste(bidr)}`);
    if (ingen.length) deler.push(`har ikke tilgang til ${liste(ingen)}`);
  }
  return { ikon: beh.length ? ROMROLLE.behandler.ikon : les.length ? ROMROLLE.leser.ikon : ROMROLLE.bidragsyter.ikon, tekst: `${start}${epost} ${liste(deler)}.${slutt}` };
}

/** Summen øverst på lista og på raden i Organisasjon. Rollene teller alle; tilstandene står for seg. */
export function summen(brukere: readonly AdminUser[]): { personer: string; roller: string; venter: number; ute: number } {
  const n = brukere.length;
  const eiere = brukere.filter(erEier).length;
  const medl = n - eiere;
  return {
    personer: `${n} ${n === 1 ? 'person' : 'personer'}`,
    roller: `${eiere} ${eiere === 1 ? 'eier' : 'eiere'}, ${medl} ${medl === 1 ? 'medlem' : 'medlemmer'}`,
    venter: brukere.filter((b) => statusFor(b) === 'venter').length,
    ute: brukere.filter((b) => statusFor(b) === 'ute').length,
  };
}

/** Grunnene på personsiden — i DOM én gang, knyttet til hver gruppe med aria-describedby. */
export const GRUNN_EGEN = 'Du kan ikke endre din egen tilgang. En annen eier må gjøre det.';
export const GRUNN_UTE = 'Kontoen er deaktivert. Aktiver den for å endre tilgangen.';
